import { useCallback, useContext, useState, useEffect, useRef } from "react"
import cn from "classnames"
import { Button } from "components/Core"
import { AppContext } from "store/context/AppContext"
import { IoIosSearch } from "react-icons/io"
import { MdFilterList } from "react-icons/md"
import { HiOutlineClock, HiChevronDown, HiOutlineSearch, HiArrowRight, HiX } from "react-icons/hi"
import useFiltersTable from "hooks/useFiltersTable"
import table_filters from "helpers/table_filters.json"
import { useLocation } from "react-router-dom"

interface ISearchBarFilter {
  handle_page_change: (page: number) => void
  classNameInput?: string
  showHistoryOnFocus?: boolean
}

const SearchBarFilter = ({ handle_page_change, classNameInput, showHistoryOnFocus = true }: ISearchBarFilter) => {
  const _table_filters: any = table_filters
  const { pathname } = useLocation()
  const { filters_table, open_table_filters, set_open_table_filters } = useContext(AppContext)
  const [value_main_search, set_value_main_search] = useState<string>("")
  const [searchHistory, setSearchHistory] = useState<any[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [historySearch, setHistorySearch] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isInteractingWithDropdown = useRef(false)
  const pathnameRef = useRef(pathname)
  // Snapshot of filter state when we arrived at this route — changes from this are the only ones we save
  const filtersBaselineRef = useRef(JSON.stringify(filters_table.state))

  const current_table_main_filter: any = _table_filters?.tables[pathname]?.mainSearchField
  const { apply_filter } = useFiltersTable({
    table_name: "orders",
    onFilterApplied: () => {
      if (handle_page_change) handle_page_change(0)
    },
  })

  // Load history when route changes and update refs
  useEffect(() => {
    pathnameRef.current = pathname
    // Capture current filters as baseline for this route — we must NOT save these to the new route's history
    filtersBaselineRef.current = JSON.stringify(filters_table.state)
    const historyKey = `search_history_${pathname}`
    const stored = localStorage.getItem(historyKey)
    try {
      setSearchHistory(stored ? JSON.parse(stored) : [])
    } catch (e) {
      setSearchHistory([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Save history ONLY when filter state diverges from the baseline for this route
  useEffect(() => {
    const currentJson = JSON.stringify(filters_table.state)
    if (currentJson === filtersBaselineRef.current) {
      // Same as when we arrived — nothing new to save
      return
    }
    filtersBaselineRef.current = currentJson
    if (filters_table.state.length > 0 && pathnameRef.current === pathname) {
      const historyKey = `search_history_${pathname}`
      setSearchHistory((prev) => {
        const jsonState = currentJson
        if (prev.length > 0 && JSON.stringify(prev[0]) === jsonState) return prev
        const newHistory = [
          filters_table.state,
          ...prev.filter((h) => JSON.stringify(h) !== jsonState),
        ].slice(0, 5)
        localStorage.setItem(historyKey, JSON.stringify(newHistory))
        return newHistory
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters_table.state])

  const handle_filters = useCallback(
    () => set_open_table_filters(!open_table_filters),
    [open_table_filters, set_open_table_filters],
  )

  const handle_change_value_main_search = (e: any) => {
    e.preventDefault()
    set_value_main_search(e?.target?.value)
  }

  const apply_filter_value_main_search = () => {
    const filterId = current_table_main_filter?.value
    if (!value_main_search?.trim()) {
      // Empty field: remove only the main search filter then reset page
      filters_table.dispatch({ type: "DELETE_FILTER", payload: filterId })
      if (handle_page_change) handle_page_change(0)
      return
    }
    apply_filter({
      ...current_table_main_filter,
      id: filterId,
      field: filterId,
      value: value_main_search?.trim(),
    })
    if (showHistoryOnFocus) {
      inputRef.current?.blur()
    } else {
      set_value_main_search("")
    }
  }

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") apply_filter_value_main_search()
  }

  const applyHistory = (historyItem: any) => {
    filters_table.dispatch({ type: "SET_FILTERS", payload: historyItem })
    if (handle_page_change) handle_page_change(0)
    setShowHistory(false)
    setHistorySearch("")
  }

  const handleInputFocus = () => {
    if (showHistoryOnFocus && searchHistory.length > 0) {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
      setShowHistory(true)
    }
  }

  const handleInputBlur = () => {
    if (showHistoryOnFocus) {
      if (isInteractingWithDropdown.current) {
        isInteractingWithDropdown.current = false
        return
      }
      blurTimeoutRef.current = setTimeout(() => setShowHistory(false), 150)
    }
  }

  const filteredHistory = searchHistory.filter((hi) =>
    JSON.stringify(hi).toLowerCase().includes(historySearch.toLowerCase()),
  )

  const clearHistory = () => {
    const historyKey = `search_history_${pathname}`
    localStorage.removeItem(historyKey)
    setSearchHistory([])
    setHistorySearch("")
    setShowHistory(false)
  }

  // Returns dropdown JSX inline (NOT a subcomponent) to avoid React unmounting on re-render
  const renderHistoryDropdown = (extraClass = "") => (
    <div
      className={cn("bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-hidden flex flex-col", extraClass)}
      onMouseDown={() => { isInteractingWithDropdown.current = true }}
    >
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-indigo-50 to-slate-50 px-4 pt-4 pb-3 border-b border-gray-100 flex flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-indigo-100 p-1.5 rounded-lg">
              <HiOutlineClock className="text-indigo-600 text-sm" />
            </div>
            <span className="text-[12px] font-bold text-gray-700 tracking-tight">Búsquedas recientes</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                clearHistory()
              }}
              className="text-[10px] font-semibold text-gray-400 hover:text-red-500 transition-colors underline underline-offset-2"
            >
              Borrar historial
            </button>
            <span className="text-[10px] font-semibold text-indigo-500 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">
              {searchHistory.length} / 5
            </span>
            <button
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowHistory(false)
                setHistorySearch("")
              }}
              className="p-1 rounded-full hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors"
              title="Cerrar"
            >
              <HiX className="text-sm" />
            </button>
          </div>
        </div>
        {/* Filter search input */}
        <div className="relative">
          <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            className="w-full bg-white border border-gray-200 rounded-xl text-[12px] pl-9 pr-3 py-2 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 transition-all placeholder-gray-400"
            placeholder="Buscar en el historial..."
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>

      {/* Results */}
      <div className="flex flex-col overflow-y-auto max-h-[300px] py-1">
        {filteredHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
            <div className="bg-gray-100 p-3 rounded-full">
              <HiOutlineSearch className="text-gray-400 text-xl" />
            </div>
            <p className="text-[12px] font-medium text-gray-400">Sin resultados</p>
            <p className="text-[11px] text-gray-300">Intenta con otro término</p>
          </div>
        ) : (
          filteredHistory.map((historyItem, idx) => (
            <button
              key={idx}
              onMouseDown={(e) => { e.preventDefault(); applyHistory(historyItem) }}
              className="group flex flex-row items-start text-left px-4 py-3 text-[12px] hover:bg-slate-50 transition-all duration-150 gap-3 border-b border-gray-50 last:border-b-0"
            >
              {/* Index badge */}
              <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-gray-100 group-hover:bg-indigo-100 flex items-center justify-center transition-colors">
                <span className="text-[9px] font-black text-gray-400 group-hover:text-indigo-600 transition-colors">{idx + 1}</span>
              </div>

              {/* Tags */}
              <div className="flex-1 flex flex-wrap gap-1.5 pt-0.5">
                {historyItem.map((h: any, i: number) => {
                  const valString = String(h.value)
                  const truncatedVal = valString.length > 35 ? valString.substring(0, 35) + "..." : valString
                  return (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 text-[11px] bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded-lg whitespace-nowrap"
                    >
                      <span className="text-indigo-400 font-normal text-[10px] uppercase tracking-wide">{h.label || h.field}</span>
                      <span className="text-indigo-300 font-light">·</span>
                      <span className="font-semibold">{truncatedVal}</span>
                    </span>
                  )
                })}
              </div>

              {/* Arrow */}
              <div className="flex-shrink-0 mt-1 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200">
                <HiArrowRight className="text-indigo-400 text-sm" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  )

  return (
    <div className="flex-1 min-w-0 flex flex-col gap-2">
      <div className="w-full flex flex-col lg:flex-row gap-4">
        <div className="w-full h-10 relative">
          <div className="relative w-full h-full outline-none">
            <IoIosSearch className="absolute text-xl text-[#ACA7A7] top-2 left-3 z-10" />
            <input
              ref={inputRef}
              type="text"
              className={cn(
                "w-full h-full outline-none border border-[#ACA7A7] rounded-2xl pl-10 pr-10 focus-visible:border-none",
                classNameInput,
              )}
              placeholder={`Buscar ${current_table_main_filter?.label}`}
              onChange={handle_change_value_main_search}
              value={value_main_search}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
            {value_main_search && (
              <button
                onMouseDown={(e) => {
                  e.preventDefault() // Prevent losing focus from input
                  set_value_main_search("")
                  const filterId = current_table_main_filter?.value
                  filters_table.dispatch({ type: "DELETE_FILTER", payload: filterId })
                  if (handle_page_change) handle_page_change(0)
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#ACA7A7] hover:text-gray-600 transition-colors z-10"
                title="Limpiar búsqueda"
              >
                <HiX className="text-lg" />
              </button>
            )}
          </div>

          {/* Focus-mode dropdown anchored below the input */}
          {showHistoryOnFocus && showHistory && searchHistory.length > 0 && (
            <div className="absolute top-12 left-0 w-full z-50">
              {renderHistoryDropdown("min-w-full max-w-full")}
            </div>
          )}
        </div>

        <div className="flex flex-row items-center justify-end gap-2">
          <Button
            onClick={apply_filter_value_main_search}
            label="Buscar"
            className="!bg-slate-600 !text-white capitalize !font-semibold !order-2 !lg:order-1"
          />
          <button
            onClick={handle_filters}
            className="relative flex flex-row order-1 lg:order-2 items-center justify-center p-2 gap-1 font-semibold text-[#4C4C4C] border border-transparent transform transition duration-500 hover:scale-105"
          >
            {Object.keys(filters_table.state).length ? <span className="notification-dot"></span> : null}
            <MdFilterList className="text-2xl" />
            <p>Filtrar</p>
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-1 relative z-30">
        {Object.keys(filters_table.state).length > 0 && (
          <div className="flex justify-start px-2">
            <button
              onClick={() => {
                filters_table.dispatch({ type: "CLEAR_FILTERS" })
                if (handle_page_change) handle_page_change(0)
              }}
              className="text-[12px] font-bold text-[#007AFF] hover:text-[#F9004D] transition-colors flex items-center gap-1.5 underline underline-offset-4"
            >
              Limpiar filtros activos
            </button>
          </div>
        )}

        {/* Button-driven history (only when NOT in focus mode) */}
        {!showHistoryOnFocus && searchHistory.length > 0 && (
          <div className="flex flex-col items-start px-2 mt-4 mb-2 relative">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-bold transition-all duration-300 border group",
                showHistory
                  ? "bg-indigo-50 border-indigo-200 text-indigo-700 shadow-inner"
                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5",
              )}
            >
              <HiOutlineClock className={cn("text-lg transition-colors", showHistory ? "text-indigo-600" : "text-gray-400 group-hover:text-gray-600")} />
              <span>Historial de Búsquedas</span>
              <span className={cn(
                "flex items-center justify-center min-w-[20px] h-5 rounded-full text-[10px] font-black transition-colors px-1.5",
                showHistory ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500 group-hover:bg-gray-200",
              )}>
                {searchHistory.length}
              </span>
              <HiChevronDown className={cn("transition-transform text-sm", showHistory && "rotate-180")} />
            </button>

            {showHistory && (
              <div className="absolute top-11 left-2 z-50 min-w-[340px] max-w-[420px]">
                {renderHistoryDropdown()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchBarFilter
